// ForDoctorsPage — Професионална страница за лекари и клиники.
// B2B портал: клинични инструменти, PDF справки, program партньорство.

import React from "react";
import { Link } from "react-router-dom";
import {
  Stethoscope,
  FileText,
  Users,
  ShieldCheck,
  Microscope,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  BookOpen,
  HeartPulse,
  GraduationCap,
  Mail,
} from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import "@/styles/landing-brand.css";

const CLINICAL_TOOLS = [
  {
    icon: FileText,
    title: "Клинична PDF справка",
    desc: "Всеки пациентски модул (Breast Health, Dental, Skin, Hematology) генерира структуриран PDF, готов за медицинско досие.",
  },
  {
    icon: Microscope,
    title: "Лабораторни интерпретации",
    desc: "OCR на лабораторни резултати от 20+ лаборатории в България. Автоматична интерпретация в контекста на digital twin мрежата.",
  },
  {
    icon: HeartPulse,
    title: "Breast Health Intelligence",
    desc: "29 модулни секции — Metabolic Score, Hereditary Risk, Density Intelligence, Tissue Age, Prevention Path и клиничен обзор.",
  },
  {
    icon: Stethoscope,
    title: "Dental Screening Suite",
    desc: "CAMBRA (кариес) + EFP 2018 (периодонтит) скорове. Детска версия до 18 г., рентген флоу, red-flag детекция.",
  },
  {
    icon: ShieldCheck,
    title: "Проследяване на пациенти",
    desc: "С разрешение на пациента получавате преглед на хранителните му навици, DIL, NIS, и симптоми между прегледите.",
  },
  {
    icon: BookOpen,
    title: "Прозрачна методология",
    desc: "Всеки индекс (NIS, DIL, Fii, COL3, PRAL) е публично документиран с формули, валидации и източници. Без черни кутии.",
  },
];

const VALUE_PROPS = [
  "Клинична PDF справка за всеки модул — директно във Вашата амбулатория",
  "Единна база лабораторни показатели от 20+ български лаборатории",
  "Пациентът идва на прегледа с готов хранителен и симптомен дневник",
  "11 digital twin модула със 110+ научни връзки между системите",
  "Прозрачни изчисления — всяка препоръка е с peer-reviewed източник",
  "GDPR-съвместима платформа, разработена с клиничен борд",
];

const PROGRAMMES = [
  {
    icon: GraduationCap,
    title: "Клинична програма",
    desc: "Включете се в научните изследвания на Nutramilo. Споделена методология, съвместни публикации, достъп до анонимизирани датасети.",
    link: "/for-doctors",
    cta: "Към програмата",
  },
  {
    icon: Users,
    title: "Експертен борд",
    desc: "Запознайте се с медицинските консултанти и научния борд. Отворена покана за специалисти в метаболизма, онкологията, диетологията.",
    link: "/consultants",
    cta: "Към експертите",
  },
];

export default function ForDoctorsPage() {
  const { isDark } = useTheme();
  return (
    <div
      data-testid="for-doctors-root"
      className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-white"}`}
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <SEO
        title="За лекари и клиники — Nutramilo"
        description="Професионален портал: клинични инструменти, PDF справки, лабораторни интерпретации, Breast Health и Dental скрининг, партньорска програма."
      />

      {/* Sticky back nav */}
      <nav
        data-testid="for-doctors-nav"
        className={`sticky top-0 z-30 backdrop-blur-xl border-b ${
          isDark
            ? "bg-[hsl(223,47%,6%)]/90 border-white/10"
            : "bg-white/90 border-slate-100"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="for-doctors-back"
            className={`inline-flex items-center gap-2 text-sm font-semibold ${
              isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-[#287a2e]"
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Към Nutramilo
          </Link>
          <Link
            to="/register?type=clinic"
            data-testid="for-doctors-nav-cta"
            className="brand-cta-primary rounded-full px-5 py-2 text-xs font-semibold"
          >
            Клинична регистрация
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section
        data-testid="for-doctors-hero"
        className={`relative overflow-hidden py-20 sm:py-28 border-b ${
          isDark
            ? "bg-[hsl(223,47%,8%)] border-white/10"
            : "bg-[#F9F9F7] border-slate-100"
        }`}
      >
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, #0e5c60 0%, #68c349 50%, transparent 70%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
            style={{
              background: "rgba(14,92,96,0.08)",
              color: "#0e5c60",
              border: "1px solid rgba(14,92,96,0.15)",
            }}
          >
            <Stethoscope className="w-3.5 h-3.5" />
            За медицински специалисти
          </div>
          <h1
            data-testid="for-doctors-title"
            className={`text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6 max-w-4xl ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            Клинични инструменти,{" "}
            <span
              style={{
                background:
                  "linear-gradient(135deg, #0e5c60 0%, #68c349 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              пациентът
            </span>{" "}
            идва готов на прегледа.
          </h1>
          <p
            className={`text-lg sm:text-xl leading-relaxed mb-8 max-w-3xl ${
              isDark ? "text-slate-300" : "text-slate-600"
            }`}
            data-testid="for-doctors-sub"
          >
            Nutramilo помага на лекарите да получат структурирана картина за
            хранителните навици, лабораторните резултати и ранните скринингови
            сигнали на техните пациенти — още преди прегледа.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/register?type=clinic">
              <button
                data-testid="for-doctors-hero-cta"
                className="brand-cta-primary rounded-full px-7 py-3 text-sm font-semibold"
              >
                Запишете клиника
                <ArrowRight className="w-4 h-4 ml-2 inline" />
              </button>
            </Link>
            <Link to="/scientific-formulas">
              <button
                data-testid="for-doctors-science-cta"
                className={`rounded-full px-7 py-3 text-sm font-semibold border ${
                  isDark
                    ? "border-white/20 text-white hover:bg-white/5"
                    : "border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
              >
                Към научната методология
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Clinical tools grid */}
      <section
        className={`py-20 ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-white"}`}
        data-testid="for-doctors-tools"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <div
              className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
              style={{ color: "#0e5c60" }}
            >
              Клинични инструменти
            </div>
            <h2
              className={`text-3xl sm:text-4xl font-bold tracking-tight mb-4 ${
                isDark ? "text-white" : "text-slate-900"
              }`}
            >
              Шест модула, създадени с клиничната практика на ум
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CLINICAL_TOOLS.map((t, i) => {
              const Icon = t.icon;
              return (
                <div
                  key={i}
                  data-testid={`for-doctors-tool-${i}`}
                  className={`p-6 rounded-2xl ${
                    isDark
                      ? "bg-[hsl(223,47%,10%)] border border-white/10"
                      : "bg-[#F9F9F7] border border-slate-100"
                  }`}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{
                      background:
                        "linear-gradient(135deg, #0e5c60 0%, #68c349 100%)",
                    }}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3
                    className={`text-base font-semibold mb-2 ${
                      isDark ? "text-white" : "text-slate-900"
                    }`}
                  >
                    {t.title}
                  </h3>
                  <p
                    className={`text-sm leading-relaxed ${
                      isDark ? "text-slate-400" : "text-slate-600"
                    }`}
                  >
                    {t.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Value props */}
      <section
        className={`py-20 ${
          isDark ? "bg-[hsl(223,47%,8%)]" : "bg-[#F9F9F7]"
        }`}
        data-testid="for-doctors-value"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2
            className={`text-3xl sm:text-4xl font-bold tracking-tight mb-10 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            Защо лекарите избират Nutramilo
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {VALUE_PROPS.map((v, i) => (
              <div
                key={i}
                data-testid={`for-doctors-value-${i}`}
                className="flex items-start gap-3"
              >
                <CheckCircle2
                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                  style={{ color: "#68c349" }}
                />
                <span
                  className={`text-base ${
                    isDark ? "text-slate-300" : "text-slate-700"
                  }`}
                >
                  {v}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programmes */}
      <section
        className={`py-20 ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-white"}`}
        data-testid="for-doctors-programmes"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2
            className={`text-3xl sm:text-4xl font-bold tracking-tight mb-10 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            Партньорство
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {PROGRAMMES.map((p, i) => {
              const Icon = p.icon;
              return (
                <Link
                  to={p.link}
                  key={i}
                  data-testid={`for-doctors-programme-${i}`}
                  className={`group p-7 rounded-2xl block transition-all hover:-translate-y-1 ${
                    isDark
                      ? "bg-[hsl(223,47%,10%)] border border-white/10 hover:border-white/20"
                      : "bg-[#F9F9F7] border border-slate-100 hover:border-slate-200 hover:shadow-md"
                  }`}
                >
                  <Icon
                    className="w-7 h-7 mb-4"
                    style={{ color: "#0e5c60" }}
                  />
                  <h3
                    className={`text-xl font-semibold mb-2 ${
                      isDark ? "text-white" : "text-slate-900"
                    }`}
                  >
                    {p.title}
                  </h3>
                  <p
                    className={`text-sm leading-relaxed mb-4 ${
                      isDark ? "text-slate-400" : "text-slate-600"
                    }`}
                  >
                    {p.desc}
                  </p>
                  <span
                    className="inline-flex items-center gap-1 text-xs font-semibold"
                    style={{ color: "#0e5c60" }}
                  >
                    {p.cta} <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section
        className={`py-20 border-t ${
          isDark
            ? "bg-[hsl(223,47%,6%)] border-white/10"
            : "bg-[#F9F9F7] border-slate-100"
        }`}
        data-testid="for-doctors-contact"
      >
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Mail
            className="w-10 h-10 mx-auto mb-4"
            style={{ color: "#0e5c60" }}
          />
          <h2
            className={`text-2xl sm:text-3xl font-bold mb-3 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            Готови сте да започнете?
          </h2>
          <p
            className={`text-base mb-6 ${
              isDark ? "text-slate-400" : "text-slate-600"
            }`}
          >
            Пишете на научния и клиничния ни екип — обсъждаме интеграции,
            пациентски обеми и клинична програма. Отговаряме в рамките на 48
            часа.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <button
                data-testid="for-doctors-contact-cta"
                className="brand-cta-primary rounded-full px-7 py-3 text-sm font-semibold"
              >
                Свържете се с нас
              </button>
            </Link>
            <Link to="/register?type=clinic">
              <button
                data-testid="for-doctors-register-cta"
                className={`rounded-full px-7 py-3 text-sm font-semibold border ${
                  isDark
                    ? "border-white/20 text-white hover:bg-white/5"
                    : "border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
              >
                Регистрирайте клиниката
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
